import * as React from "react";
import { type ReactNode, useState } from "react";

import type * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import { ValidatedForm, useField } from "@rvf/react-router";

import { cn } from "~/lib/ui";

import { CrossedEyeIcon, EyeIcon } from "../ui/icons/eye-icon";
import { TickIcon } from "../ui/icons/tick";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FormErrors } from "./FormErrors";
import { GlobalErrors } from "./GlobalErrors";

/**
 * Usage:
 * 
 * 
 <Form>
 <input type="hidden" name="intent" value="changeAccountDetails" />
 <GlobalErrors data?={actionData}/>
 <FormErrors />
 <FormField
  name="username">
    {(field)=>
      <FormItem>
        <FormLabel>Username</FormLabel>
        <FormControl>
          <Input placeholder="shadcn"  />
        </FormControl>
        <FormDescription>This is your public display name.</FormDescription>
        <FormMessage />
      </FormItem>
    }
  </FormField>

  // or

  <FormField
  name="username">
      <FormItem>
        <FormLabel>Username</FormLabel>
        <FormControl>
          <Input />
        </FormControl>
        <FormDescription>This is your public display name.</FormDescription>
        <FormMessage />
      </FormItem>
  </FormField>

  <InputField name="username" label="Username" description="This is your public display name." />
  </Form>
 */

const Form = ValidatedForm;
export const PasswordField = ({
  name,
  label,
  description,
  placeholder,
  isHidden,
  type: _type, //ignore the type and default to password
  ...props
}: {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
  isHidden?: boolean;
  type?: "password";
} & Omit<React.ComponentProps<typeof Input>, "type">) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  return (
    <FormField name={name}>
      <FormItem>
        {label && (
          <FormLabel className="text-base font-semibold">{label}</FormLabel>
        )}
        <div className="relative">
          <FormControl>
            <Input
              placeholder={placeholder}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              {...props}
            />
          </FormControl>
          {
            <button
              className="absolute inset-y-0 right-0 mr-4 flex items-center"
              onClick={e => {
                e.preventDefault();
                setShowPassword(prev => !prev);
              }}
            >
              {showPassword ? <CrossedEyeIcon /> : <EyeIcon />}
            </button>
          }
        </div>
        {description && <FormDescription>{description}</FormDescription>}
        <FormMessage />
      </FormItem>
    </FormField>
  );
};
export const InputField = ({
  name,
  label,
  description,
  placeholder,
  isHidden,
  ...props
}: {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
  isHidden?: boolean;
} & React.ComponentProps<typeof Input>) => (
  <FormField name={name}>
    <FormItem>
      {label && (
        <FormLabel className="text-base font-semibold text-contrast-black">
          {label}
        </FormLabel>
      )}
      <FormControl>
        <Input placeholder={placeholder} {...props} />
      </FormControl>
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  </FormField>
);
export const CheckboxField = ({
  name,
  label,
  description,
  placeholder,
  ...props
}: {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
} & Omit<React.ComponentProps<typeof Input>, "type">) => (
  <FormField name={name} className="w-auto">
    <FormItem className="flex items-center space-y-0">
      <FormControl>
        <div className="relative mt-2">
          <TickIcon className="pointer-events-none absolute left-[4px] top-1 z-10" />

          <Input
            placeholder={placeholder}
            type="checkbox"
            name={name}
            {...props}
            className="pointer-events-auto relative z-20 mr-2 inline-block h-[20px] w-[20px] appearance-none rounded border-outline p-0 shadow-none checked:z-0 checked:border-none checked:bg-checked"
          />
        </div>
      </FormControl>
      {label && (
        <FormLabel className="inline align-super font-semibold text-contrast-black">
          {label}
        </FormLabel>
      )}
      {description && <FormDescription>{description}</FormDescription>}
      <FormMessage />
    </FormItem>
  </FormField>
);

export type FieldType = ReturnType<typeof useFormField>;
type FormFieldContextValue = {
  name: string;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);
const FormItemRender = ({
  children,
}: {
  children: ReactNode | ((field: FieldType) => ReactNode);
}) => {
  const field = useFormField();
  const body = typeof children === "function" ? children(field) : children;
  return body;
};
const FormField = ({
  name,
  children,
  className,
}: {
  name: string;
  children: ReactNode | ((field: FieldType) => ReactNode);
  className?: string;
}) => {
  return (
    <div className={cn("my-2", className)}>
      <FormFieldContext.Provider value={{ name: name }}>
        <FormItemRender>{children}</FormItemRender>
      </FormFieldContext.Provider>
    </div>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const fieldState = useField(fieldContext.name);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
    name: fieldContext.name,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

const FormItem = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"div">) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-1", className)} {...props} />
    </FormItemContext.Provider>
  );
};
FormItem.displayName = "FormItem";

const FormLabel = ({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) => {
  const { error, formItemId } = useFormField();

  const errorMessage = error();
  return (
    <Label
      ref={ref}
      className={cn(
        "pb-0 text-base font-normal",
        errorMessage && "text-destructive",
        className,
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
};
FormLabel.displayName = "FormLabel";

const FormControl = ({ ref, ...props }: React.ComponentProps<typeof Slot>) => {
  const {
    error,
    formItemId,
    formDescriptionId,
    formMessageId,
    getInputProps,
    name,
  } = useFormField();
  const formProps = getInputProps();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      name={name}
      {...formProps}
      {...props}
    />
  );
};
FormControl.displayName = "FormControl";

const FormDescription = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"p">) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn("text-[0.8rem] text-muted-foreground", className)}
      {...props}
    />
  );
};
FormDescription.displayName = "FormDescription";

const FormMessage = ({
  className,
  children,
  ref,
  ...props
}: React.ComponentProps<"p">) => {
  const { error, formMessageId } = useFormField();
  const errorMessage = error();
  const body = errorMessage ? String(errorMessage) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-[0.8rem] font-medium text-destructive", className)}
      {...props}
    >
      {body}
    </p>
  );
};
FormMessage.displayName = "FormMessage";

export {
  Form,
  FormControl,
  FormDescription,
  FormErrors,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  GlobalErrors,
  useFormField,
};
